<template>
  <div id="app">
    <header id="header" class="has-background-light pt-5">
      <div class="container">
        <h1 id="header-title" class="title is-size-4">
          <router-link :to="{ path: '/' }" class="has-text-dark">
            Databases Workbench
            <span v-if="environment" class="tag is-link ml-1" style="vertical-align: bottom;">
              {{ environment }}
            </span>
          </router-link>
        </h1>
        <img id="header-logo" class="rsc-logo" alt="Royal Society of Chemistry"
          src="@/styles/icons/rsc_logo.svg" />
        <nav class="tabs is-boxed">
          <ul id="header-nav">
            <li
              v-for="tab in navigation"
              :key="tab.path"
              :class="{ 'router-link-exact-active is-active': $route.path === tab.path }"
            >
              <router-link
                :to="tab.path"
                :id="`header-nav-${tab.id.toLowerCase()}`"
              >
                {{ tab.label }}
              </router-link>
            </li>
          </ul>
        </nav>
      </div>
    </header>
    <div class="container py-6">
      <UserInfo />
      <router-view :id="this.$route.meta.elementId" />
    </div>
  </div>
</template>

<script>
import { UserInfo } from '@/components/auth';
import { useHead } from '@unhead/vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/store/modules/auth';

export default {
  name: 'App',
  setup() {
    const authStore = useAuthStore();

    useHead({
      title: 'Databases Workbench',
      titleTemplate: (title) => {
        if (title === 'Databases Workbench') return title
        return `${title} - Databases Workbench`
      }
    });

    // Detect route changes and enforce fallback if the new route doesn't define a title
    const router = useRouter();
    router.afterEach((to) => {
    // If the target route has no meta title, restore the fallback
      if (!to.meta.title) {
        useHead({ title: 'Databases Workbench' });
      }
    });

    return {
      authStore,
    };

  },
  data() {
    return {
      tabs: [
        {
          id: 'MarinLit',
          label: 'MarinLit',
          path: '/marinlit',
          exact: false,
        },
        {
          id: 'MerckIndex',
          label: 'Merck Index',
          path: '/merckindex',
          exact: false,
        },
        {
          id: 'CSDepositions',
          label: 'CS Depositions',
          path: '/cs-depositions',
          exact: false
        },
        {
          id: 'Curation',
          label: 'CS Curation',
          path: '/curation/data-sources',
          exact: false
        }
      ],
    }
  },
  computed: {
    userProducts() {
      return this.authStore.userProducts;
    },
    environment() {
      return import.meta.env.PROD
        ? import.meta.env.VITE_ENVIRONMENT
        : ''
    },
    navigation() {
      // Fallback for local development: shows all tabs without ENTRA
      let products = this.userProducts;
      // if (!import.meta.env.PROD && !products.length) {
      //   products = this.tabs.map(tab => tab.id)
      // }
      if (!import.meta.env.PROD && (!products || !products.length)) {
        products = this.tabs.map(tab => tab.id)
      }
      // end of fallback 

      // normal behaviour outside local. Original code:
      const links = this.tabs.filter(tab => products.includes(tab.id));
      links.unshift({ id: 'Home', label: 'Home', path: '/', exact: true });
      return links;
    },
  },
  methods: {
    setCurrentUser(value) {
      this.authStore.setCurrentUser(value);
    },
    setUserGroups(value) {
      this.authStore.setUserGroups(value);
    },
    async updateAuth() {
      if (this.authState.isAuthenticated) {
        this.setCurrentUser(await this.$auth.getUser());
        this.setUserGroups(this.authState.accessToken.claims.groups);
      }
    },  
  },
  watch: {
    // Wait until authState is ready before updating user info
    'authState.isPending': function () {
      this.updateAuth();
    },
  },
  mounted() {
    // Renews the token every 59 minutes
    setInterval(() => {
      this.authStore.updateAuth();
    }, 60 * 59 * 1000);
  },
  components: {
    UserInfo,
  },
}
</script>

<style lang="scss">
@import '@/styles/index.scss';
</style>
